import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Modal,
  Card,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Paginations from "../../common/pagination/Paginations";
import { useSelector } from "react-redux";
import { getPopularProductApi, venderProductDetailApi } from "../../api";
import { AdminTable, ActionCell } from "../../common/adminTable";
import UserImg from "../../common/assests/sampleuser.svg";
import configuration from "../../config/Config";

const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const decimal = rating - fullStars;
  const stars = [];
  for (let i = 0; i < fullStars; i++) {
    stars.push(<StarIcon key={i} sx={{ color: "#FFD700", fontSize: "20px" }} />);
  }
  if (decimal > 0) {
    stars.push(<StarHalfIcon key="partial" sx={{ color: "#FFD700", fontSize: "20px" }} />);
  }
  for (let i = stars.length; i < 5; i++) {
    stars.push(<StarOutlineIcon key={`e${i}`} sx={{ color: "#FFD700", fontSize: "20px" }} />);
  }
  return <Box sx={{ display: "flex" }}>{stars}</Box>;
};

const PopularProduct = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const [popularProducts, setPopularProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [productDetail, setProductDetails] = useState({});
  const [open, setOpen] = useState(false);

  const fetchPopularProducts = async () => {
    try {
      setLoading(true);
      const response = await getPopularProductApi({ token, currentPage, limit: 10 });
      setLoading(false);
      setPopularProducts(response?.data?.products || []);
      setCurrentPage(response?.data?.currentPage);
      setTotalPages(response?.data?.totalPages);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchPopularProducts();
  }, [currentPage]);

  const handleProductDetails = async (id) => {
    try {
      const response = await venderProductDetailApi({ token, id });
      setProductDetails(response);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleClickOpen = (item) => {
    setSelectedRow(item);
    handleProductDetails(item?._id);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const columns = [
    {
      field: "photo",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.photos?.length > 0 ? `${configuration.cdnFront}/${row.photos[0].url}` : UserImg}
          alt="product"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Name", minWidth: 130, renderCell: (r) => r?.title || "-" },
    {
      field: "vendor",
      headerName: "Vendor Name",
      minWidth: 130,
      renderCell: (r) => r?.seller?.fullname || r?.userId?.fullname || "-",
    },
    { field: "category", headerName: "Category", width: 120, renderCell: (r) => r?.category?.name || r?.categoryId?.name || "-" },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      cellSx: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
      renderCell: (r) => r?.description || "-",
    },
    {
      field: "rating",
      headerName: "Review & Rating",
      width: 160,
      renderCell: (r) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StarRating rating={r?.rating || 0} />
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>{r?.rating || 0}</Typography>
        </Box>
      ),
    },
    { field: "price", headerName: "Price", width: 90, renderCell: (r) => `$${r?.price || "-"}` },
    { field: "discountedProce", headerName: "Discounted Price", width: 130, renderCell: (r) => `$${r?.discountedProce || "-"}` },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px", marginTop: "40px", marginBottom: "30px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography sx={{ fontSize: { xs: "16px", sm: "22px" }, fontWeight: "600", color: "#362B24" }}>
            All Popular Products
          </Typography>
        </Box>

        <AdminTable
          columns={columns}
          rows={popularProducts}
          loading={loading}
          rowKey="_id"
          emptyText="No products found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            maxWidth: "675px",
            width: "90%",
            maxHeight: "80vh",
            bgcolor: "#F5F5F5",
            borderRadius: "24px",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: 24,
            p: "20px",
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>Product Details</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedRow && (
            <Box sx={{ marginTop: "20px" }} key={selectedRow?._id}>
              <img
                src={selectedRow?.photos?.length > 0 ? `${configuration.cdnFront}/${selectedRow.photos[0].url}` : UserImg}
                alt="product"
                style={{ width: "100%", height: "auto", maxHeight: "231px", borderRadius: "24px", marginBottom: "20px" }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 0fr))", gap: "5px" }}>
                {selectedRow?.photos?.map((image) => (
                  <Box
                    key={image?._id}
                    sx={{
                      width: "77px",
                      height: "79px",
                      flexShrink: 0,
                      borderRadius: "16px",
                      boxShadow: "3px 2px 20px 0px rgba(0, 0, 0, 0.04)",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={image?.url ? `${configuration.cdnFront}/${image.url}` : UserImg}
                      alt="product"
                      style={{ width: "100%", height: "100%", maxHeight: "80px", objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 0fr))", gap: 3, marginTop: "25px" }}>
                {[
                  { label: "Product Name", value: selectedRow?.title },
                  { label: "Product Category", value: selectedRow?.category?.name || selectedRow?.categoryId?.name },
                  { label: "Price", value: `$${selectedRow?.price || "-"}` },
                  { label: "Discount Price", value: `$${selectedRow?.discountedProce || "-"}` },
                ].map(({ label, value }) => (
                  <Box key={label}>
                    <Typography sx={{ fontWeight: "400", fontSize: "13px", opacity: "0.6", color: "#362B24" }}>{label}</Typography>
                    <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{value || "-"}</strong></Typography>
                  </Box>
                ))}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: "16px", color: "#362B24", marginTop: "20px" }}>Description</Typography>
                <Typography sx={{ fontSize: "13px", opacity: "0.5", color: "#362B24", fontWeight: "400", lineHeight: "24px", wordWrap: "break-word" }}>
                  {selectedRow?.description || "N/A"}
                </Typography>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: "16px", color: "#362B24", marginTop: "20px" }}>
                  Review & Rating
                </Typography>
                {productDetail?.reviews?.length > 0 ? (
                  productDetail.reviews.map((reviewItem) => (
                    <React.Fragment key={reviewItem?._id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginTop: "10px",
                          gap: "20px",
                          flexWrap: "wrap",
                          paddingBottom: "10px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "20px" }}>
                          <img
                            src={reviewItem?.user?.picture ? `${configuration.cdnFront}/${reviewItem.user.picture}` : UserImg}
                            alt="reviewer"
                            style={{ width: "46px", height: "46px", borderRadius: "50%" }}
                          />
                          <Box>
                            <Typography sx={{ fontWeight: "700", fontSize: "14px", color: "#362B24" }}>
                              {reviewItem?.user?.fullname || "N/A"}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: "#065F46" }} />
                              <Typography sx={{ fontSize: "12px", color: "#065F46", fontWeight: "400" }}>
                                {reviewItem?.createdAt
                                  ? new Date(reviewItem.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                                  : "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <StarRating rating={reviewItem?.rating || 0} />
                          <Typography sx={{ fontWeight: "600", fontSize: "15px" }}>{reviewItem?.rating || "0"}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ borderBottom: "1px solid #E0E0E0" }}>
                        <Typography sx={{ fontSize: "13px", opacity: "0.5", color: "#362B24", fontWeight: "400", lineHeight: "24px", marginBottom: "5px" }}>
                          {reviewItem?.review || "N/A"}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))
                ) : (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>No Reviews Available</div>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default PopularProduct;
